/**
 * useAuditLog Hook - Phase 2C-3
 *
 * Fetches audit log entries with filtering and pagination.
 */

import { useState, useEffect, useCallback } from 'react';
import { AuditLogEntry, AuditLogFilters } from '../types';
import { AuditLogService } from '../api';

interface UseAuditLogReturn {
  entries: AuditLogEntry[];
  total: number;
  hasMore: boolean;
  loading: boolean;
  error: string | null;
  page: number;
  pageSize: number;
  setPage: (page: number) => void;
  setPageSize: (size: number) => void;
  filters: AuditLogFilters;
  setFilters: (filters: AuditLogFilters) => void;
  refetch: () => void;
  actionTypes: string[];
  loadingActionTypes: boolean;
}

export function useAuditLog(): UseAuditLogReturn {
  const [entries, setEntries] = useState<AuditLogEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [filters, setFilters] = useState<AuditLogFilters>({});
  const [actionTypes, setActionTypes] = useState<string[]>([]);
  const [loadingActionTypes, setLoadingActionTypes] = useState(true);

  // Fetch action types for filter dropdown
  useEffect(() => {
    const fetchActionTypes = async () => {
      try {
        const types = await AuditLogService.getActionTypes();
        setActionTypes(types);
      } catch (err) {
        console.error('Failed to load action types:', err);
      } finally {
        setLoadingActionTypes(false);
      }
    };
    fetchActionTypes();
  }, []);

  const fetchEntries = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const offset = (page - 1) * pageSize;
      const response = await AuditLogService.getAll({
        admin: filters.admin_user_id,
        action: filters.action,
        target_type: filters.target_type,
        target_id: filters.target_id,
        start_date: filters.start_date,
        end_date: filters.end_date,
        limit: pageSize,
        offset,
      });
      setEntries(response.entries);
      setTotal(response.total);
      setHasMore(response.has_more);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load audit log');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, filters]);

  useEffect(() => {
    fetchEntries();
  }, [fetchEntries]);

  return {
    entries,
    total,
    hasMore,
    loading,
    error,
    page,
    pageSize,
    setPage,
    setPageSize,
    filters,
    setFilters,
    refetch: fetchEntries,
    actionTypes,
    loadingActionTypes,
  };
}
